import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Users, CreditCard, Building2, TrendingUp, UserPlus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { Loader2 } from "lucide-react";

interface UserSignup {
  id: string;
  email: string;
  full_name: string | null;
  phone: string | null;
  created_at: string;
  org_id: string | null;
  org_name?: string;
  subscription_status?: string;
  stripe_subscription_id?: string | null;
}

interface PlatformStats {
  totalUsers: number;
  payingUsers: number;
  trialUsers: number;
  freeUsers: number;
  totalOrgs: number;
  totalRevenue: number;
  signupsThisMonth: number;
  signupsThisWeek: number;
}

export function PlatformAnalyticsDashboard() {
  const [users, setUsers] = useState<UserSignup[]>([]);
  const [stats, setStats] = useState<PlatformStats>({
    totalUsers: 0,
    payingUsers: 0,
    trialUsers: 0,
    freeUsers: 0,
    totalOrgs: 0,
    totalRevenue: 0,
    signupsThisMonth: 0,
    signupsThisWeek: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      // Get all profiles with org info
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("id, email, full_name, phone, created_at, org_id")
        .order("created_at", { ascending: false });

      if (profilesError) throw profilesError;

      // Get all organizations with subscription info
      const { data: orgs, error: orgsError } = await supabase
        .from("organizations")
        .select("id, name, subscription_status, stripe_subscription_id, stripe_customer_id");

      if (orgsError) throw orgsError;

      // Map org data to profiles
      const orgMap = new Map(orgs?.map(o => [o.id, o]) || []);
      
      const enrichedUsers: UserSignup[] = (profiles || []).map(profile => {
        const org = profile.org_id ? orgMap.get(profile.org_id) : null;
        return {
          ...profile,
          phone: profile.phone || null,
          org_name: org?.name || null,
          subscription_status: org?.subscription_status || null,
          stripe_subscription_id: org?.stripe_subscription_id || null,
        };
      });

      setUsers(enrichedUsers);

      // Calculate stats
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const startOfWeek = new Date(now);
      startOfWeek.setDate(now.getDate() - 7);

      const payingOrgs = orgs?.filter(o => o.stripe_subscription_id) || [];
      const trialOrgs = orgs?.filter(o => o.subscription_status === 'trial' && !o.stripe_subscription_id) || [];
      
      setStats({
        totalUsers: profiles?.length || 0,
        payingUsers: payingOrgs.length,
        trialUsers: trialOrgs.length,
        freeUsers: (orgs?.length || 0) - payingOrgs.length - trialOrgs.length,
        totalOrgs: orgs?.length || 0,
        totalRevenue: 0, // Would need Stripe integration to calculate
        signupsThisMonth: profiles?.filter(p => new Date(p.created_at) >= startOfMonth).length || 0,
        signupsThisWeek: profiles?.filter(p => new Date(p.created_at) >= startOfWeek).length || 0,
      });

    } catch (error: any) {
      console.error("Failed to load platform analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (user: UserSignup) => {
    if (user.stripe_subscription_id) {
      return <Badge className="bg-success text-success-foreground">Paying</Badge>;
    }
    if (user.subscription_status === 'trial') {
      return <Badge variant="secondary">Trial</Badge>;
    }
    if (user.subscription_status === 'active') {
      return <Badge variant="outline">Active (No Payment)</Badge>;
    }
    if (!user.org_id) {
      return <Badge variant="destructive">No Org</Badge>;
    }
    return <Badge variant="outline">Free</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Sign-ups</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalUsers}</div>
            <p className="text-xs text-muted-foreground">
              +{stats.signupsThisWeek} this week
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Paying Customers</CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.payingUsers}</div>
            <p className="text-xs text-muted-foreground">
              {stats.totalOrgs > 0 ? ((stats.payingUsers / stats.totalOrgs) * 100).toFixed(0) : 0}% conversion
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Trial Users</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.trialUsers}</div>
            <p className="text-xs text-muted-foreground">
              Potential conversions
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Organizations</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalOrgs}</div>
            <p className="text-xs text-muted-foreground">
              {stats.signupsThisMonth} new this month
            </p>
          </CardContent>
        </Card>
      </div>


      {/* Recent Signups Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            <CardTitle>All Sign-ups</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Organization</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Signed Up</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{user.full_name || "—"}</p>
                      <p className="text-sm text-muted-foreground">{user.email}</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{user.phone || "—"}</TableCell>
                  <TableCell>{user.org_name || "—"}</TableCell>
                  <TableCell>{getStatusBadge(user)}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {format(new Date(user.created_at), "MMM d, yyyy")}
                  </TableCell>
                </TableRow>
              ))}
              {users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                    No sign-ups yet
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
